import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '../api/client'
import { getKbApi } from '../api/kb'
import type {
  KbAction,
  KbEntity,
  KbFields,
  KbKind,
  KbLogEntry,
  KbState,
  KbTryResult,
} from '../api/kb'

export type KbSection = 'menu' | 'facts' | 'answers' | 'rules' | 'try' | 'history'

/** Calm Thai message for a failed call (the server's own detail when it has one). */
export function kbErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 0) return e.message
    if (e.status === 403) return e.message || 'ไม่มีสิทธิ์ทำรายการนี้'
    if (e.status >= 500) return 'ระบบขัดข้อง ลองใหม่อีกครั้ง'
    return e.message
  }
  return 'ทำรายการไม่สำเร็จ ลองใหม่อีกครั้ง'
}

/**
 * The knowledge page "คลังความรู้ AI": one state object from the server, replaced after every change
 * (the server returns the whole state). Busy keys (`kind:id` / `new:kind` / `undo:id`) disable just the
 * button being pressed; the page keeps showing the last good data.
 */
export const useKnowledge = defineStore('knowledge', () => {
  const data = ref<KbState | null>(null)
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  const busy = ref<Set<string>>(new Set())
  const toast = ref<string | null>(null)
  let toastTimer: ReturnType<typeof setTimeout> | null = null

  const log = ref<KbLogEntry[]>([])
  const logNext = ref<string | null>(null)
  const logEntity = ref<KbEntity | null>(null)
  const logLoading = ref(false)
  const logLoaded = ref(false)

  const tryResult = ref<KbTryResult | null>(null)
  const trying = ref(false)
  const tryError = ref<string | null>(null)
  const recent = ref<string[]>([])

  const me = computed(() => data.value?.me ?? null)
  const canEdit = computed(() => me.value?.can_edit ?? false)
  const canApprove = computed(() => me.value?.can_approve ?? false)

  /** Waiting for the owner, per section (new rows + edits of live rows). */
  const pending = computed(() => {
    const d = data.value
    const n = (rows: { status: string; pending: unknown }[]) =>
      rows.filter((r) => r.status === 'pending_approval' || r.pending).length
    return { facts: d ? n(d.facts) : 0, answers: d ? n(d.answers) : 0, rules: d ? n(d.rules) : 0 }
  })

  function flash(msg: string): void {
    toast.value = msg
    if (toastTimer) clearTimeout(toastTimer)
    toastTimer = setTimeout(() => (toast.value = null), 3200)
  }

  function isBusy(key: string): boolean {
    return busy.value.has(key)
  }

  async function withBusy<T>(key: string, fn: () => Promise<T>): Promise<T | null> {
    if (busy.value.has(key)) return null
    busy.value = new Set(busy.value).add(key)
    try {
      return await fn()
    } catch (e) {
      flash(kbErrorText(e))
      return null
    } finally {
      const next = new Set(busy.value)
      next.delete(key)
      busy.value = next
    }
  }

  /** Every state taken (a load or an action's answer): a load still on its way never overwrites a newer one. */
  let stateSeq = 0
  let lastLoad = 0

  async function load(): Promise<void> {
    const n = ++lastLoad
    const at = stateSeq
    loading.value = true
    try {
      const s = await getKbApi().load()
      if (n !== lastLoad || at !== stateSeq) return // a newer load, or an approve / edit answered meanwhile
      stateSeq++
      data.value = s
      loadError.value = null
    } catch (e) {
      if (n === lastLoad) loadError.value = kbErrorText(e)
    } finally {
      if (n === lastLoad) loading.value = false
    }
  }

  function after(state: KbState | null, msg: string): boolean {
    if (!state) return false
    stateSeq++
    data.value = state
    logLoaded.value = false // the history tab reloads next time it opens
    flash(msg)
    return true
  }

  const waits = (): boolean => !canApprove.value

  async function create(kind: KbKind, fields: KbFields): Promise<boolean> {
    const s = await withBusy(`new:${kind}`, () => getKbApi().create(kind, fields))
    return after(s, waits() ? 'ส่งให้เจ้าของอนุมัติแล้ว' : 'เพิ่มแล้ว AI เริ่มใช้ภายในไม่กี่วินาที')
  }

  async function edit(kind: KbKind, id: string, fields: KbFields, live: boolean): Promise<boolean> {
    const s = await withBusy(`${kind}:${id}`, () => getKbApi().edit(kind, id, fields))
    const msg = waits()
      ? live
        ? 'ส่งให้เจ้าของอนุมัติแล้ว AI ยังใช้ฉบับเดิมจนกว่าจะอนุมัติ'
        : 'บันทึกข้อเสนอแล้ว'
      : 'บันทึกแล้ว'
    return after(s, msg)
  }

  async function act(kind: KbKind, id: string, action: KbAction, fields?: KbFields): Promise<boolean> {
    const s = await withBusy(`${kind}:${id}`, () => getKbApi().act(kind, id, action, fields))
    const msg: Record<KbAction, string> = {
      approve: 'อนุมัติแล้ว AI เริ่มใช้ภายในไม่กี่วินาที',
      replace: 'ใช้ข้อใหม่แทนข้อเดิมแล้ว ข้อเดิมปิดใช้ (ย้อนกลับได้ในประวัติการแก้)',
      reject: canApprove.value ? 'ไม่อนุมัติแล้ว AI ไม่ใช้ข้อนี้' : 'ยกเลิกข้อเสนอแล้ว',
      retire: canApprove.value ? 'ปิดใช้แล้ว' : 'ส่งให้เจ้าของอนุมัติการปิดใช้แล้ว',
      restore: 'เปิดใช้อีกครั้งแล้ว',
    }
    return after(s, msg[action])
  }

  let logSeq = 0
  async function loadLog(reset = true): Promise<void> {
    if (logLoading.value && !reset) return // "more": the page on its way first
    const n = ++logSeq // a new first page (another filter) wins over one still on its way
    logLoading.value = true
    try {
      const page = await getKbApi().log({ entity: logEntity.value, before: reset ? null : logNext.value })
      if (n !== logSeq) return
      log.value = reset ? page.items : [...log.value, ...page.items]
      logNext.value = page.next_before
      logLoaded.value = true
    } catch (e) {
      if (n === logSeq) flash(kbErrorText(e))
    } finally {
      if (n === logSeq) logLoading.value = false
    }
  }

  function setLogEntity(e: KbEntity | null): void {
    if (logEntity.value === e) return
    logEntity.value = e
    void loadLog(true)
  }

  async function undo(logId: string): Promise<boolean> {
    const s = await withBusy(`undo:${logId}`, () => getKbApi().undo(logId))
    const ok = after(s, 'ย้อนกลับแล้ว')
    if (ok) await loadLog(true)
    return ok
  }

  async function ask(question: string): Promise<void> {
    const q = question.trim()
    if (q.length < 2 || trying.value) return
    trying.value = true
    tryError.value = null
    try {
      tryResult.value = await getKbApi().ask(q)
      recent.value = [q, ...recent.value.filter((x) => x !== q)].slice(0, 5)
    } catch (e) {
      tryError.value = kbErrorText(e)
    } finally {
      trying.value = false
    }
  }

  return {
    data, loading, loadError, toast, me, canEdit, canApprove, pending,
    log, logNext, logEntity, logLoading, logLoaded,
    tryResult, trying, tryError, recent,
    flash, isBusy, load, create, edit, act, loadLog, setLogEntity, undo, ask,
  }
})
